// reCAPTCHA Enterprise token source for the login surface (magic-link +
// contact). Invisible + score-based: when a public site key is configured at
// build (VITE_RECAPTCHA_SITE_KEY) it loads the Enterprise script once and mints
// a fresh token per action. With no key it is inert — token() returns '' and
// the caller omits the header; the backend, also disabled, fails open. The
// default reCAPTCHA badge satisfies Google's attribution requirement, so no
// extra notice copy is needed.

export const RECAPTCHA_HEADER = 'X-Recaptcha-Token';

interface Enterprise {
  ready(cb: () => void): void;
  execute(siteKey: string, opts: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise: Enterprise };
  }
}

function siteKey(): string {
  return import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
}

let loader: Promise<void> | null = null;

function loadScript(key: string): Promise<void> {
  if (loader) return loader;
  loader = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('recaptcha: script load failed'));
    document.head.appendChild(s);
  });
  return loader;
}

// token returns a reCAPTCHA Enterprise token for action, or '' when reCAPTCHA is
// unconfigured or fails to load/execute. The caller sends the header only when
// the token is non-empty; the backend fails open regardless, so a transient
// failure never blocks the user.
export async function token(action: string): Promise<string> {
  const key = siteKey();
  if (!key) return '';
  try {
    if (!window.grecaptcha) await loadScript(key);
    const g = window.grecaptcha;
    if (!g) return '';
    return await new Promise<string>((resolve) => {
      g.enterprise.ready(() => {
        g.enterprise.execute(key, { action }).then(resolve, () => resolve(''));
      });
    });
  } catch {
    return '';
  }
}

// recaptchaHeader resolves to the header map to merge into a request, or
// undefined when no token is available — so a caller can pass it straight to the
// transport's optional `headers` without a branch.
export async function recaptchaHeader(action: string): Promise<Record<string, string> | undefined> {
  const t = await token(action);
  return t ? { [RECAPTCHA_HEADER]: t } : undefined;
}
